class BanzrcTtMediaGallery {
  constructor(owner) {
    this.owner = owner;
    this.selectedColor = null;
    this.slideCache = null;
    this.thumbnailCache = new WeakMap();
    this.zoomCache = null;
    this.observer = new MutationObserver(() => this.scheduleApply());
    if (this.owner.section) this.observer.observe(this.owner.section, { childList: true, subtree: true });
    this.scheduleApply();
  }

  disconnect() {
    this.observer?.disconnect();
    window.cancelAnimationFrame(this.applyFrame);
  }

  setColor(color) {
    const normalizedColor = this.normalizeColor(color);
    if (normalizedColor === this.selectedColor) return;
    this.selectedColor = normalizedColor;
    this.scheduleApply();
  }

  normalizeColor(value) {
    return String(value || '')
      .replace(/\s+RTR$/i, '')
      .trim()
      .toLowerCase();
  }

  sourceKey(value) {
    if (!value) return '';
    try {
      return decodeURIComponent(new URL(value, window.location.origin).pathname.split('/').pop() || '').toLowerCase();
    } catch (_) {
      return String(value).split('?')[0].split('/').pop().toLowerCase();
    }
  }

  scheduleApply() {
    window.cancelAnimationFrame(this.applyFrame);
    this.applyFrame = window.requestAnimationFrame(() => this.apply());
  }

  orderedEntries() {
    const configuredLimit = Number(this.owner.dataset.galleryMediaLimit || 0);
    const sources = [...this.owner.querySelectorAll('[data-tt-gallery-source]')]
      .map((source) => ({
        key: this.sourceKey(source.dataset.src),
        color: this.normalizeColor(source.dataset.ttGalleryColor),
      }))
      .filter((source) => source.key);
    const limited = configuredLimit > 0 ? sources.slice(0, configuredLimit) : sources;
    const hideVariants = this.owner.dataset.galleryHideVariants === 'true';

    if (!hideVariants) return limited;
    const activeColor = this.selectedColor || 'red';

    return limited
      .filter((source) => source.color === 'shared' || source.color === activeColor)
      .sort((left, right) => {
        const leftPrimary = left.color === activeColor ? 0 : 1;
        const rightPrimary = right.color === activeColor ? 0 : 1;
        return leftPrimary - rightPrimary;
      });
  }

  imageKey(element) {
    const image = element?.querySelector('img');
    return this.sourceKey(image?.currentSrc || image?.getAttribute('src'));
  }

  reorder(parent, elements) {
    if (!parent || !elements.length) return;
    const current = [...parent.children].filter((child) => elements.includes(child));
    if (current.length === elements.length && current.every((child, index) => child === elements[index])) return;
    elements.forEach((element) => parent.append(element));
  }

  apply() {
    const gallery = this.owner.section?.querySelector('media-gallery');
    const slideshow = gallery?.querySelector(':scope > slideshow-component');
    const scroller = slideshow?.querySelector(':scope > slideshow-container > slideshow-slides');
    if (!gallery || !slideshow || !scroller) return;

    if (!this.slideCache || this.slideCache.gallery !== gallery) {
      this.slideCache = { gallery, slides: [...scroller.querySelectorAll(':scope > slideshow-slide')] };
      this.thumbnailCache = new WeakMap();
      this.zoomCache = null;
    }
    const allSlides = this.slideCache.slides;
    const sourceElements = [...this.owner.querySelectorAll('[data-tt-gallery-source]')];
    const configuredLimit = Number(this.owner.dataset.galleryMediaLimit || 0);
    const sourceEntries = sourceElements
      .map((source) => ({ key: this.sourceKey(source.dataset.src), color: this.normalizeColor(source.dataset.ttGalleryColor) }))
      .filter((source) => source.key);
    const limitedSources = configuredLimit > 0 ? sourceEntries.slice(0, configuredLimit) : sourceEntries;
    const desiredEntries = this.orderedEntries();
    const desiredKeys = desiredEntries.map((entry) => entry.key);
    const curatedKeys = new Set(limitedSources.map((entry) => entry.key));
    const slideByKey = new Map(allSlides.map((slide) => [this.imageKey(slide), slide]));

    let orderedSlides;
    if (curatedKeys.size) {
      allSlides.forEach((slide) => {
        if (!curatedKeys.has(this.imageKey(slide))) slide.remove();
      });
      orderedSlides = desiredKeys.map((key) => slideByKey.get(key)).filter(Boolean);
    } else {
      const limit = configuredLimit > 0 ? configuredLimit : allSlides.length;
      orderedSlides = allSlides.slice(0, limit);
      allSlides.slice(limit).forEach((slide) => slide.remove());
    }

    const orderedSet = new Set(orderedSlides);
    [...scroller.querySelectorAll(':scope > slideshow-slide')].forEach((slide) => {
      if (!orderedSet.has(slide)) slide.remove();
    });
    this.reorder(scroller, orderedSlides);
    orderedSlides.forEach((slide, index) => {
      slide.hidden = false;
      const action = slide.getAttribute('on:click');
      if (action) slide.setAttribute('on:click', action.replace(/\/open\/\d+$/, `/open/${index}`));
    });

    this.updateControls(slideshow, desiredKeys, orderedSlides.length, curatedKeys.size > 0);
    this.updateZoom(gallery, desiredKeys, curatedKeys.size > 0);
    this.owner.dataset.visibleMediaCount = String(orderedSlides.length);
    slideshow.select?.(0, undefined, { animate: false });
  }

  updateControls(slideshow, desiredKeys, visibleCount, hasCuratedSources) {
    slideshow.querySelectorAll('slideshow-controls').forEach((controls) => {
      const thumbnailContainer = controls.querySelector('.slideshow-controls__thumbnails');
      if (thumbnailContainer) {
        let thumbnails = this.thumbnailCache.get(controls);
        if (!thumbnails) {
          thumbnails = [...thumbnailContainer.querySelectorAll(':scope > button')];
          this.thumbnailCache.set(controls, thumbnails);
        }
        let orderedThumbnails;
        if (hasCuratedSources) {
          const byKey = new Map(thumbnails.map((thumbnail) => [this.imageKey(thumbnail), thumbnail]));
          thumbnails.forEach((thumbnail) => {
            if (!desiredKeys.includes(this.imageKey(thumbnail))) thumbnail.remove();
          });
          orderedThumbnails = desiredKeys.map((key) => byKey.get(key)).filter(Boolean);
        } else {
          orderedThumbnails = thumbnails.slice(0, visibleCount);
          thumbnails.slice(visibleCount).forEach((thumbnail) => thumbnail.remove());
        }
        this.reorder(thumbnailContainer, orderedThumbnails);
        orderedThumbnails.forEach((thumbnail, index) => {
          thumbnail.hidden = false;
          thumbnail.setAttribute('on:click', `/select/${index}`);
          thumbnail.setAttribute('aria-selected', String(index === 0));
          thumbnail.setAttribute('aria-label', `Slide ${index + 1} of ${visibleCount}`);
        });
      }

      const dots = [...controls.querySelectorAll('.slideshow-controls__dots > li')];
      dots.forEach((dot, index) => {
        dot.hidden = index >= visibleCount;
        const button = dot.querySelector('button');
        if (button && index < visibleCount) {
          button.setAttribute('on:click', `/select/${index}`);
          button.setAttribute('aria-selected', String(index === 0));
        }
      });

      const counter = controls.querySelector('.slideshow-controls__counter');
      if (counter) {
        const current = counter.querySelector('[ref="current"]');
        if (current) current.textContent = '1';
        if (counter.lastChild?.nodeType === Node.TEXT_NODE) counter.lastChild.textContent = String(visibleCount);
      }
    });
  }

  updateZoom(gallery, desiredKeys, hasCuratedSources) {
    const dialog = gallery.querySelector('zoom-dialog dialog');
    if (!dialog) return;
    const thumbnailParent = dialog.querySelector('.dialog-thumbnails-list');
    const zoomList = dialog.querySelector('.dialog-zoomed-gallery');
    if (!this.zoomCache || this.zoomCache.dialog !== dialog) {
      this.zoomCache = {
        dialog,
        thumbnails: [...(thumbnailParent?.querySelectorAll(':scope > button') || [])],
        zoomItems: [...(zoomList?.querySelectorAll(':scope > li') || [])],
      };
    }
    const thumbnails = this.zoomCache.thumbnails;
    const zoomItems = this.zoomCache.zoomItems;
    const count = desiredKeys.length;

    const orderByKey = (elements, parent) => {
      if (!parent) return [];
      if (!hasCuratedSources) {
        elements.slice(count).forEach((element) => element.remove());
        return elements.slice(0, count);
      }
      const byKey = new Map(elements.map((element) => [this.imageKey(element), element]));
      elements.forEach((element) => {
        if (!desiredKeys.includes(this.imageKey(element))) element.remove();
      });
      const ordered = desiredKeys.map((key) => byKey.get(key)).filter(Boolean);
      this.reorder(parent, ordered);
      return ordered;
    };

    orderByKey(thumbnails, thumbnailParent).forEach((thumbnail, index) => {
      thumbnail.hidden = false;
      thumbnail.setAttribute('on:click', `/handleThumbnailClick/${index}`);
      thumbnail.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
      thumbnail.setAttribute('aria-selected', String(index === 0));
    });
    orderByKey(zoomItems, zoomList).forEach((item) => {
      item.hidden = false;
    });
  }
}

class BanzrcGtConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      this.section = this.closest('.shopify-section');
      this.mediaGallery = new BanzrcTtMediaGallery(this);
      this.observePurchaseFeedback();
      this.render();
      return;
    }
    this.dataset.initialized = 'true';

    this.section = this.closest('.shopify-section');
    this.removeStrayMediaNote();
    this.preservePreviewView();
    this.mediaGallery = new BanzrcTtMediaGallery(this);
    this.rows = Array.from(this.querySelectorAll('[data-variant-row]')).map((row) => ({
      id: row.dataset.variantId,
      color: row.dataset.color,
      power: row.dataset.power,
      available: row.dataset.available === 'true',
      price: row.dataset.price,
      inventory: Number(row.dataset.inventory || 0),
    }));
    this.selectedColor = null;
    this.selectedPower = null;
    this.feedbackTimer = null;
    this.previewVariantId = null;

    this.querySelectorAll('[data-color]').forEach((button) => {
      if (!button.matches('button')) return;
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.cancelPendingSync();
        this.selectedColor = button.dataset.color;
        this.render();
        if (!this.selectedPower) this.previewNativeColor(this.selectedColor);
      });
    });

    this.querySelectorAll('[data-power]').forEach((button) => {
      if (!button.matches('button')) return;
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.cancelPendingSync();
        this.selectedPower = button.dataset.power;
        this.render();
      });
    });

    const variantFromUrl = new URL(window.location.href).searchParams.get('variant');
    const linkedVariant = this.rows.find((row) => row.id === variantFromUrl);
    if (linkedVariant) {
      this.selectedColor = linkedVariant.color;
      this.selectedPower = linkedVariant.power;
    }

    this.observePurchaseFeedback();
    this.render();
  }

  get selectedVariant() {
    if (!this.selectedColor || !this.selectedPower) return null;
    return this.rows.find((row) => row.color === this.selectedColor && row.power === this.selectedPower) || null;
  }

  get currentSection() {
    if (!this.isConnected) return null;
    return this.closest('.shopify-section');
  }

  removeStrayMediaNote() {
    const media = this.currentSection?.querySelector('.product-information__media');
    if (!media) return;
    const strayNotes = new Set(['需要翻译并未更新', '需要翻译并未重新']);
    [...media.childNodes].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && strayNotes.has(node.textContent.trim())) node.remove();
    });
  }

  preservePreviewView() {
    const previewView = new URL(window.location.href).searchParams.get('view');
    const nativeVariantPicker = this.currentSection?.querySelector('variant-picker[data-product-url]');
    if (!previewView || !nativeVariantPicker?.dataset.productUrl) return;
    const productUrl = new URL(nativeVariantPicker.dataset.productUrl, window.location.origin);
    productUrl.searchParams.set('view', previewView);
    nativeVariantPicker.dataset.productUrl = `${productUrl.pathname}${productUrl.search}`;
  }

  render() {
    const variant = this.selectedVariant;
    const ready = Boolean(variant && variant.available);

    this.querySelectorAll('button[data-color]').forEach((button) => {
      const color = button.dataset.color;
      const possible = this.rows.some(
        (row) => row.color === color && (!this.selectedPower || row.power === this.selectedPower) && row.available
      );
      button.disabled = !possible;
      button.setAttribute('aria-pressed', String(color === this.selectedColor));
    });

    this.querySelectorAll('button[data-power]').forEach((button) => {
      const power = button.dataset.power;
      const possible = this.rows.some(
        (row) => row.power === power && (!this.selectedColor || row.color === this.selectedColor) && row.available
      );
      button.disabled = !possible;
      button.setAttribute('aria-pressed', String(power === this.selectedPower));
    });

    const colorValue = this.querySelector('[data-color-value]');
    const powerValue = this.querySelector('[data-power-value]');
    if (colorValue) colorValue.textContent = this.selectedColor || '';
    if (powerValue) powerValue.textContent = this.selectedPower || '';
    this.dataset.selectionComplete = String(ready);

    this.updateSelectionMessage(variant);
    this.updateSystemDetails(variant);
    this.mediaGallery?.setColor(this.selectedColor);
    if (ready) {
      this.selectNativeVariant(variant);
    } else {
      this.syncNativeVariantInput(null);
    }
    this.updatePurchaseControls(ready);
  }

  updateSelectionMessage(variant) {
    const message = this.querySelector('[data-selection-message]');
    if (!message) return;
    const copy = (key, fallback) => this.getAttribute(`data-${key}`) || fallback;
    if (!this.selectedColor && !this.selectedPower) {
      message.textContent = copy('select-color-power', 'Choose a color and power system.');
    } else if (!this.selectedColor) {
      message.textContent = copy('select-color', 'Choose a color.');
    } else if (!this.selectedPower) {
      message.textContent = copy('select-power', 'Choose a power system.');
    } else if (!variant || !variant.available) {
      message.textContent = copy('unavailable', 'This configuration is not available.');
    } else {
      message.textContent = `${variant.color} / ${variant.power} ${copy('selected-suffix', 'selected.')}`;
    }
  }

  updateSystemDetails(variant) {
    const power = this.selectedPower;
    const systemKey = power ? power.toLowerCase() : null;
    const setText = (selector, value) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    };

    setText('[data-price]', variant ? variant.price : this.getAttribute('data-choose-configuration') || 'Choose configuration');
    setText(
      '[data-inventory]',
      variant
        ? variant.available
          ? `${this.getAttribute('data-in-stock') || 'In stock'} — ${variant.inventory} available`
          : this.getAttribute('data-out-of-stock') || 'Out of stock'
        : this.getAttribute('data-choose-configuration') || 'Choose configuration'
    );
    const selectPower = this.getAttribute('data-select-power') || 'Choose power system';
    setText('[data-motor]', systemKey ? this.getAttribute(`data-motor-${systemKey}`) : selectPower);
    setText('[data-esc]', systemKey ? this.getAttribute(`data-esc-${systemKey}`) : selectPower);
    setText('[data-servo]', systemKey ? this.getAttribute(`data-servo-${systemKey}`) : selectPower);
    setText(
      '[data-battery-detail]',
      systemKey ? this.getAttribute(`data-battery-${systemKey}`) : this.getAttribute('data-battery-prompt') || 'Select 6S or 8S to confirm the compatible voltage range.'
    );
  }

  selectNativeVariant(variant) {
    this.cancelPendingSync();
    this.preservePreviewView();
    const input = this.currentSection?.querySelector(`variant-picker input[data-variant-id="${CSS.escape(variant.id)}"]`);
    const nativeInput = this.currentSection?.querySelector('form[action*="/cart/add"] input[ref="variantId"]');
    const nativeVariantIsCurrent = nativeInput?.value === variant.id && !nativeInput.disabled;
    if (!input) return;
    if (!nativeVariantIsCurrent) {
      if (input.checked) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        input.click();
      }
    }
    this.syncNativeVariantInput(variant);
  }

  previewNativeColor(color) {
    this.cancelPendingSync();
    this.preservePreviewView();
    const previewVariant =
      this.rows.find((row) => row.color === color && row.power === '6S' && row.available) ||
      this.rows.find((row) => row.color === color && row.available);
    if (!previewVariant) return;

    const input = this.currentSection?.querySelector(
      `variant-picker input[data-variant-id="${CSS.escape(previewVariant.id)}"]`
    );
    if (!input || input.checked) return;
    this.previewVariantId = previewVariant.id;
    input.click();

    this.previewTimer = window.setTimeout(() => {
      if (this.selectedPower || this.previewVariantId !== previewVariant.id) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === previewVariant.id) {
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }
      this.syncNativeVariantInput(null);
      this.updatePurchaseControls(false);
    }, 1200);
  }

  cancelPendingSync() {
    if (this.previewTimer) window.clearTimeout(this.previewTimer);
    this.previewTimer = null;
    this.previewVariantId = null;
  }

  syncNativeVariantInput(variant) {
    const input = this.currentSection?.querySelector('form[action*="/cart/add"] input[ref="variantId"]');
    if (!input) return;
    const nextValue = variant?.id || '';
    if (input.value !== nextValue) input.value = nextValue;
    if (variant?.id) {
      if (input.getAttribute('value') !== variant.id) input.setAttribute('value', variant.id);
      if (input.disabled) input.disabled = false;
    } else {
      if (input.hasAttribute('value')) input.removeAttribute('value');
      if (!input.disabled) input.disabled = true;
    }
  }

  updatePurchaseControls(enabled) {
    const section = this.currentSection;
    const button = section?.querySelector('add-to-cart-component button');
    const accelerated = section?.querySelector('.accelerated-checkout-block');
    if (button) {
      button.disabled = !enabled;
      button.setAttribute('aria-disabled', String(!enabled));
    }
    if (accelerated) accelerated.hidden = !enabled;
  }

  observePurchaseFeedback() {
    if (!this.section) return;
    this.syncNativeVariantAfterSelect = (event) => {
      const sync = () => {
        const variant = this.selectedVariant;
        if (!this.isConnected || !variant?.available) return;
        this.syncNativeVariantInput(variant);
        this.updatePurchaseControls(true);
      };
      if (event.promise?.then) {
        event.promise.then(
          () => queueMicrotask(sync),
          () => queueMicrotask(sync)
        );
      }
    };
    this.section.addEventListener('shopify:product:select', this.syncNativeVariantAfterSelect);
    this.observer = new MutationObserver((mutations) => {
      this.updatePurchaseControls(this.dataset.selectionComplete === 'true');
      const wasAdded = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-added' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.dataset.added === 'true'
      );
      if (wasAdded) this.showCartFeedback();
    });
    this.observer.observe(this.section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-added'],
    });
  }

  showCartFeedback() {
    const feedback = this.querySelector('[data-cart-feedback]');
    if (!feedback) return;
    feedback.hidden = false;
    window.clearTimeout(this.feedbackTimer);
    this.feedbackTimer = window.setTimeout(() => {
      feedback.hidden = true;
    }, 5000);
  }

  disconnectedCallback() {
    this.mediaGallery?.disconnect();
    this.observer?.disconnect();
    if (this.section && this.syncNativeVariantAfterSelect) {
      this.section.removeEventListener('shopify:product:select', this.syncNativeVariantAfterSelect);
    }
    window.clearTimeout(this.feedbackTimer);
    this.cancelPendingSync();
  }
}

if (!customElements.get('banzrc-gt-configurator')) {
  customElements.define('banzrc-gt-configurator', BanzrcGtConfigurator);
}
